import React, { useRef } from 'react';
import classnames from 'classnames';
import type { PositionableProps, PrimaryPlacement } from '../../types';
import PopoverArrow from '../PopoverArrow';
import PopoverContainer from '../PopoverContainer';
import s from './DefaultPopoverContent.scss';

export interface Props extends PositionableProps {
  arrowClassName?: string;
  wrapperClassName?: string;
  contentClassName?: string;
  oneLine?: boolean;
  noPadding?: boolean;
  noMinWidth?: boolean;
  noMaxWidth?: boolean;
  showArrow?: boolean;
  width?: React.CSSProperties['width'];
  height?: React.CSSProperties['height'];
  directionalClassNames?: {
    [k in PrimaryPlacement]?: string;
  };
  role?: string;
  'aria-label'?: string;
  children: React.ReactNode;
  onClickOutside?: (event: MouseEvent) => void;
  updatePositioning?: () => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

/** This is our basic popover with the default styles applied */
const DefaultPopoverContent: React.FC<Props> = ({
  effectivePlacement,
  arrowClassName,
  wrapperClassName,
  contentClassName,
  oneLine,
  noPadding,
  noMinWidth,
  noMaxWidth,
  width,
  height,
  showArrow,
  arrowRef,
  arrowStyle,
  arrowAttributes,
  children,
  rootRef,
  contentStyle,
  contentAttributes,
  onClickOutside,
  directionalClassNames,
  role,
  'aria-label': ariaLabel,
  onMouseEnter,
  onMouseLeave,
  onKeyDown,
  updatePositioning,
}) => {
  const contentRef = useRef<HTMLElement | null>(null);
  return (
    <PopoverContainer
      ref={rootRef}
      placement={effectivePlacement}
      style={contentStyle}
      attributes={contentAttributes}
      directionalClassNames={directionalClassNames}
      role={role}
      aria-label={ariaLabel}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={onKeyDown}
      innerRef={contentRef}
      wrapperClassName={classnames(
        s['popper-wrapper'],
        showArrow && s[`arrow-${effectivePlacement}`],
        wrapperClassName
      )}
      contentClassName={classnames(
        s['popper-content'],
        oneLine && s['one-line'],
        !noPadding && s['with-padding'],
        !noMinWidth && s['with-min-width'],
        !noMaxWidth && s['with-max-width'],
        contentClassName
      )}
      width={width}
      height={height}
      arrow={
        showArrow && (
          <PopoverArrow
            ref={arrowRef}
            placement={effectivePlacement}
            contentWidth={contentRef?.current?.clientWidth}
            contentHeight={contentRef?.current?.clientHeight}
            className={classnames(s['arrow'], arrowClassName)}
            style={arrowStyle}
            attributes={arrowAttributes}
          />
        )
      }
      onClickOutside={onClickOutside}
      updatePositioning={updatePositioning}
    >
      {children}
    </PopoverContainer>
  );
};
DefaultPopoverContent.displayName = 'DefaultPopoverContent';

export default DefaultPopoverContent;
